
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";

const suggestionSchema = z.object({
  title: z.string().min(5, "Title must be at least 5 characters"),
  description: z.string().min(20, "Description must be at least 20 characters"),
  category: z.string().optional(),
});

type SuggestionFormData = z.infer<typeof suggestionSchema>;

const SuggestFeature = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const form = useForm<SuggestionFormData>({
    resolver: zodResolver(suggestionSchema),
    defaultValues: {
      title: "",
      description: "",
      category: "",
    },
  });

  const onSubmit = async (data: SuggestionFormData) => {
    setIsSubmitting(true);

    try {
      const { data: user } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('suggestions')
        .insert({
          title: data.title,
          description: data.description,
          category: data.category || null,
          user_id: user.user?.id || null,
        });

      if (error) {
        console.error('Error submitting suggestion:', error);
        toast({
          title: "Error",
          description: "Failed to submit your suggestion. Please try again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success!",
          description: "Your feature suggestion has been submitted. Thank you for your input!",
        });
        form.reset();
      }
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const categories = [
    { value: "core", label: "Core Features" },
    { value: "filtering", label: "Filtering" },
    { value: "metrics", label: "Metrics" },
    { value: "display", label: "Display" },
    { value: "tutorials", label: "Tutorials" },
    { value: "user", label: "User Experience" },
    { value: "parts", label: "Parts Database" },
    { value: "knowledge-base", label: "Knowledge Base" },
    { value: "interactive", label: "Interactive Features" },
    { value: "community", label: "Community" },
    { value: "accessibility", label: "Accessibility" },
    { value: "other", label: "Other" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Suggest a Feature - 3DP Hub</title>
        <meta name="description" content="Help improve 3DP Hub by suggesting new features and functionality for the 3D printing community." />
      </Helmet>
      
      <Header 
        pageName="Suggest a Feature"
        useBackButton={true}
      />
      
      <main className="flex-1 py-8 pt-[76px]">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-3">Suggest a Feature</h1>
            <p className="text-lg text-muted-foreground">
              Help us build a better platform by sharing your ideas
            </p>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Feature Suggestion</CardTitle>
              <CardDescription>
                We value community input and want to hear your ideas for improving 3DP Hub. 
                Your suggestions help us prioritize development and create features that matter most to our users.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Feature Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Brief, descriptive title for your feature idea" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category (Optional)</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {categories.map((category) => (
                              <SelectItem key={category.value} value={category.value}>
                                {category.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Describe your feature idea in detail. What problem would it solve? How would it work? Who would benefit from it?"
                            className="min-h-[120px]"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end gap-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => form.reset()}
                      disabled={isSubmitting}
                      className="border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                    >
                      Clear Form
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? "Submitting..." : "Submit Suggestion"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>

          <div className="mt-8 bg-muted rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-2">What happens next?</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Your suggestion will be reviewed by our development team</li>
              <li>Popular suggestions may be added to our public roadmap</li>
              <li>We'll consider feasibility, impact, and community benefit</li>
              <li>Implementation timeline depends on complexity and priorities</li>
            </ul>
          </div>
        </div>
      </main>
      
      <footer className="bg-card border-t border-border py-6 mt-auto">
        <div className="container">
          <p className="text-sm text-center text-muted-foreground">
            3DP Hub | The ultimate directory for 3D printer parts and upgrades
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SuggestFeature;
